import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import Button from './Button';
import Card from './Card';
import TextField from './TextField';
import {
  FileUpload as FileUploadIcon,
  Code as CodeIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const LessonsArrayJsonImport = ({ moduleIndex, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const { t } = useTranslation(['courses', 'common']);

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setJsonText('');
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setShowStructure(false);
  };

  const handleImport = async () => {
    try {
      if (!jsonText.trim()) {
        setError(t('courses:lessonsImport.validation.emptyJson'));
        return;
      }
      let parsedData;
      try {
        parsedData = JSON.parse(jsonText);
      } catch (err) {
        setError(t('courses:lessonsImport.validation.invalidJson') + err.message);
        return;
      }
      if (!Array.isArray(parsedData)) {
        setError(t('courses:lessonsImport.validation.notArray'));
        return;
      }
      let hasErrors = false;
      parsedData.forEach((lesson, idx) => {
        if (!lesson.title) {
          setError(
            t('courses:lessonsImport.validation.missingTitle', { index: idx + 1 })
          );
          hasErrors = true;
        }
      });
      if (hasErrors) return;
      setLoading(true);
      const lessonsWithTempIds = parsedData.map((lesson) => ({
        ...lesson,
      }));
      if (onSuccess) {
        onSuccess(moduleIndex, lessonsWithTempIds);
        toast.success(
          t('courses:lessonsImport.success.imported', {
            count: lessonsWithTempIds.length,
          })
        );
        handleClose();
      }
      setLoading(false);
    } catch (err) {
      console.error('Ошибка при импорте уроков:', err);
      if (err.message?.includes('JSON')) {
        setError(t('courses:lessonsImport.validation.invalidJson') + err.message);
      } else {
        setError(
          t('courses:lessonsImport.validation.importError') +
            (err.message || t('courses:lessonsImport.validation.unknownError'))
        );
      }
      setLoading(false);
    }
  };

  const toggleStructure = () => {
    setShowStructure(!showStructure);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        setJsonText(content);
        JSON.parse(content);
        setError('');
      } catch (err) {
        setError(t('courses:lessonsImport.validation.invalidJson') + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Пример универсальной структуры массива уроков для импорта
  const jsonStructure = [
    {
      title: t('courses:lessonsImport.example.title'),
      content: t('courses:lessonsImport.example.content'),
      duration: 15,
      type: t('courses:lessonsImport.example.type'),
      contentType: t('courses:lessonsImport.example.contentType'),
      videoUrl: 'https://video-url.com', // для видео-уроков
      'contentBlocks (если contentType: mixed)': [
        {
          type: 'standard',
          content: t('courses:lessonsImport.example.blockContent'),
          order: 0,
        },
      ],
      resources: [
        {
          title: t('courses:lessonsImport.example.resourceTitle'),
          type: t('courses:lessonsImport.example.resourceType'),
          description: t('courses:lessonsImport.example.resourceDescription'),
          url: 'https://resource-url.com',
        },
      ],
      practiceExercises: [
        {
          title: t('courses:lessonsImport.example.exerciseTitle'),
          description: t('courses:lessonsImport.example.exerciseDescription'),
          codeSnippet: t('courses:lessonsImport.example.codeSnippet'),
          playgroundUrl: 'https://playground-url.com',
        },
      ],
    },
  ];
  
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        sx={{ 
          ml: 1,
          fontSize: '0.75rem',
          minWidth: '122px',
          minHeight: '35px',
          lineHeight: 1,
          py: 0.5,
          px: 1.5,
          borderColor: 'button.secondaryBorder',
          color: 'button.secondaryText',
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: 'button.secondaryBorder',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
      >
        {t('courses:lessonsImport.button.import')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CodeIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:lessonsImport.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('courses:lessonsImport.subtitle')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Button
              variant="contained"
              component="label"
              startIcon={<FileUploadIcon />}
              size="medium"
            >
              {t('courses:lessonsImport.button.upload')}
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleUpload}
              />
            </Button>
            <Tooltip title={t('courses:lessonsImport.button.showStructure')}>
              <IconButton onClick={toggleStructure}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            multiline
            fullWidth
            rows={12}
            value={jsonText}
            onChange={(e) => {
              if (error) setError('');
              setJsonText(e.target.value);
            }}
            variant="outlined"
            error={!!error}
            placeholder={t('courses:lessonsImport.placeholder')}
            sx={{ mb: 1 }}
          />
          {showStructure && (
            <Card sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('courses:lessonsImport.structureExample')}
              </Typography>
              <TextField
                multiline
                fullWidth
                rows={10}
                value={JSON.stringify(jsonStructure, null, 2)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                  },
                }}
              />
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined" size="large">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            size="large"
            disabled={!jsonText.trim() || !!error || loading}
          >
            {loading
              ? t('courses:lessonsImport.button.importing')
              : t('courses:lessonsImport.button.importAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LessonsArrayJsonImport;
